import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { StatsGrid, DataTable, SquarePlot, ColorbarLegend } from "../common";
import { transpose, createPhaseMapLayout, scientificPlotConfig } from "../../lib/plotUtils";
import type { PhaseResults, ConfigInfo } from "../../types/session";

interface PhaseMapPlotProps {
  results: PhaseResults;
  configInfo: ConfigInfo;
}

export const PhaseMapPlot: React.FC<PhaseMapPlotProps> = ({ results, configInfo }) => {
  const hasData = results.phase_map_notilt && results.phase_map_notilt.length > 0;

  const phaseNotiltT = useMemo(
    () => (hasData ? transpose(results.phase_map_notilt) : []),
    [hasData, results.phase_map_notilt]
  );

  const phaseNotiltdefT = useMemo(
    () => (hasData ? transpose(results.phase_map_notiltdef) : []),
    [hasData, results.phase_map_notiltdef]
  );

  const N = useMemo(
    () => (hasData ? results.phase_map_notilt.length : 0),
    [hasData, results.phase_map_notilt]
  );

  const pdiam = configInfo.pdiam;

  const notiltValues = useMemo(() => {
    if (!hasData) return [];
    return phaseNotiltT.flat().filter((v) => !isNaN(v) && isFinite(v));
  }, [hasData, phaseNotiltT]);

  const notiltdefValues = useMemo(() => {
    if (!hasData) return [];
    return phaseNotiltdefT.flat().filter((v) => !isNaN(v) && isFinite(v));
  }, [hasData, phaseNotiltdefT]);

  const minValNotilt = useMemo(
    () => (notiltValues.length > 0 ? Math.min(...notiltValues) : 0),
    [notiltValues]
  );

  const maxValNotilt = useMemo(
    () => (notiltValues.length > 0 ? Math.max(...notiltValues) : 0),
    [notiltValues]
  );

  const minValNotiltdef = useMemo(
    () => (notiltdefValues.length > 0 ? Math.min(...notiltdefValues) : 0),
    [notiltdefValues]
  );

  const maxValNotiltdef = useMemo(
    () => (notiltdefValues.length > 0 ? Math.max(...notiltdefValues) : 0),
    [notiltdefValues]
  );

  const phaseCardLayout = useMemo(
    () => ({
      ...createPhaseMapLayout(N, pdiam, ""),
      margin: { l: 0, r: 0, t: 0, b: 0 },
    }),
    [N, pdiam]
  );

  const cleanPlotConfig = useMemo(
    () => ({
      ...scientificPlotConfig,
      displayModeBar: false,
    }),
    []
  );

  const heatmapData = (z: number[][]) => ({
    z,
    type: "heatmap" as const,
    colorscale: "RdBu",
    reversescale: true,
    showscale: false,
    hovertemplate: "x: %{x}<br>y: %{y}<br>Phase: %{z:.2f} nm<extra></extra>",
  });

  if (!hasData) {
    return (
      <div className="text-muted-foreground flex h-64 items-center justify-center">
        No phase data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Card className="border-accent-purple/20">
            <CardHeader className="bg-accent-purple/5 pb-3">
              <CardTitle className="text-accent-purple text-sm">
                Phase without Tip-Tilt
                <br />({results.rms_stats.weighted_notilt.toFixed(1)} nm rms)
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 p-4">
              <SquarePlot
                data={[heatmapData(phaseNotiltT)]}
                layout={phaseCardLayout}
                config={cleanPlotConfig}
              />
              <ColorbarLegend
                colorscale="RdBu"
                reversescale={true}
                min={minValNotilt}
                max={maxValNotilt}
                title="Phase (nm)"
                orientation="h"
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-2">
          <Card className="border-accent-purple/20">
            <CardHeader className="bg-accent-purple/5 pb-3">
              <CardTitle className="text-accent-purple text-sm">
                Phase without Tip-Tilt-Defocus
                <br />({results.rms_stats.weighted_notiltdef.toFixed(1)} nm rms)
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 p-4">
              <SquarePlot
                data={[heatmapData(phaseNotiltdefT)]}
                layout={phaseCardLayout}
                config={cleanPlotConfig}
              />
              <ColorbarLegend
                colorscale="RdBu"
                reversescale={true}
                min={minValNotiltdef}
                max={maxValNotiltdef}
                title="Phase (nm)"
                orientation="h"
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <StatsGrid
        title="Phase Statistics"
        stats={[
          {
            label: "RMS (raw)",
            value: results.rms_stats.raw,
            unit: "nm",
            precision: 1,
          },
          {
            label: "RMS (weighted)",
            value: results.rms_stats.weighted,
            unit: "nm",
            precision: 1,
          },
          {
            label: "RMS without TT (raw)",
            value: results.rms_stats.raw_notilt,
            unit: "nm",
            precision: 1,
          },
          {
            label: "RMS without TT (weighted)",
            value: results.rms_stats.weighted_notilt,
            unit: "nm",
            precision: 1,
          },
          {
            label: "RMS w/o TT+Def (raw)",
            value: results.rms_stats.raw_notiltdef,
            unit: "nm",
            precision: 1,
          },
          {
            label: "RMS w/o TT+Def (weighted)",
            value: results.rms_stats.weighted_notiltdef,
            unit: "nm",
            precision: 1,
          },
        ]}
        columns={2}
      />

      <DataTable
        columns={[
          { key: "component", label: "", type: "text" },
          { key: "nm_rms", label: "nm rms", type: "number", precision: 1 },
          { key: "lambda_D", label: "λ/D", type: "number", precision: 3 },
          { key: "pixels", label: "pixels", type: "number", precision: 3 },
          { key: "mm", label: "mm", type: "number", precision: 4 },
        ]}
        data={[
          {
            component: "Tip (horizontal)",
            nm_rms: results.tiptilt_defocus_stats.tip.nm_rms,
            lambda_D: results.tiptilt_defocus_stats.tip.lambda_D,
            pixels: results.tiptilt_defocus_stats.tip.pixels,
            mm: results.tiptilt_defocus_stats.tip.mm,
          },
          {
            component: "Tilt (vertical)",
            nm_rms: results.tiptilt_defocus_stats.tilt.nm_rms,
            lambda_D: results.tiptilt_defocus_stats.tilt.lambda_D,
            pixels: results.tiptilt_defocus_stats.tilt.pixels,
            mm: results.tiptilt_defocus_stats.tilt.mm,
          },
          {
            component: "Defocus",
            nm_rms: results.tiptilt_defocus_stats.defocus.nm_rms,
            lambda_D: null,
            pixels: results.tiptilt_defocus_stats.defocus.pixels,
            mm: results.tiptilt_defocus_stats.defocus.mm,
          },
        ]}
        compact
      />
    </div>
  );
};
